import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Button, Row, Col, message } from "antd";
import { BackwardOutlined, RetweetOutlined, SaveOutlined } from "@ant-design/icons";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { useSelector } from "react-redux";
import axiosInstance from "../../../../api/Axios";

const { Option } = Select;

const AddLocationModal = ({ open, onCancel, onSuccess, organizationId, initialData, isEdit }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState("");
    const [selectedState, setSelectedState] = useState("");
    const userInfo = useSelector((state) => state.user);

    // Reset or populate form when modal opens
    useEffect(() => {
        if (open) {
            if (isEdit && initialData) {
                form.setFieldsValue({
                    ...initialData,
                    country: initialData.country,
                    state: initialData.state,
                    zipcode: initialData.zip_code || initialData.zipcode,
                });
                setSelectedCountry(initialData.country || "");
                setSelectedState(initialData.state || "");
            } else {
                form.resetFields();
                form.setFieldsValue({ country: "United States" });
                setSelectedCountry("United States");
                setSelectedState("");
            }
        }
    }, [open, form, isEdit, initialData]);

    const handleCountryChange = (val) => {
        setSelectedCountry(val);
        form.setFieldsValue({ country: val, state: "" });
        setSelectedState("");
    };

    const handleStateChange = (val) => {
        setSelectedState(val);
        form.setFieldsValue({ state: val });
    };

    const getCookie = (name) => {
        const cookies = document.cookie.split("; ");
        for (let i = 0; i < cookies.length; i++) {
            const [cookieName, cookieValue] = cookies[i].split("=");
            if (cookieName === name) {
                return cookieValue;
            }
        }
        return null;
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        const csrfToken = getCookie("csrftoken");
        try {
            const payload = {
                user: userInfo?.data?.id,
                organization: parseInt(organizationId, 10),
                address_1: values.address_1,
                address_2: values.address_2 || "",
                city: values.city,
                state: values.state,
                zipcode: values.zipcode,
                country: values.country,
                location_name: values.location_name,
            };


            if (isEdit) {
                await axiosInstance.put(`/customer/location/?id=${initialData.id}`, payload, {
                    headers: { "X-CSRFToken": csrfToken },
                });
                message.success("Location updated successfully");
            } else {
                await axiosInstance.post("/customer/location/", payload, {
                    headers: { "X-CSRFToken": csrfToken },
                });
                message.success("Location added successfully");
            }

            if (onSuccess) onSuccess();
            onCancel();
        } catch (error) {
            console.error("Error saving location:", error);
            message.error(isEdit ? "Failed to update location" : "Failed to add location");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={isEdit ? "EDIT TRAINING LOCATION" : "MANAGE TRAINING LOCATIONS"}
            open={open}
            onCancel={onCancel}
            footer={null}
            width={700}
            destroyOnClose
        >
            <div style={{ marginTop: "20px" }}>
                <h4 style={{ color: "#faad14", marginBottom: "20px" }}>Details</h4>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark={false}
                >
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item
                                name="address_1"
                                label="ADDRESS"
                                rules={[{ required: true, message: "Please enter address" }]}
                            >
                                <Input placeholder="Enter address" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="zipcode"
                                label="ZIP/POSTAL CODE"
                                rules={[{ required: true, message: "Please enter zip code" }]}
                            >
                                <Input placeholder="Enter zip code" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item
                                name="city"
                                label="CITY"
                                rules={[{ required: true, message: "Please enter city" }]}
                            >
                                <Input placeholder="Enter city" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="country"
                                label="COUNTRY"
                                rules={[{ required: true, message: "Please select country" }]}
                            >
                                <CountryDropdown
                                    value={selectedCountry}
                                    onChange={handleCountryChange}
                                    className="my-form-input-antd-test"
                                    style={{
                                        height: "3rem",
                                        border: "0.1rem solid gray",
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item
                                name="state"
                                label="STATE"
                                rules={[{ required: true, message: "Please select state" }]}
                            >
                                <RegionDropdown
                                    country={selectedCountry}
                                    value={selectedState}
                                    onChange={handleStateChange}
                                    className="my-form-input-antd-test"
                                    style={{
                                        height: "3rem",
                                        border: "0.1rem solid gray",
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="location_name"
                                label="LOCATION"
                                rules={[{ required: true, message: "Please enter location name" }]}
                            >
                                <Input placeholder="Enter location name" />
                            </Form.Item>
                        </Col>
                    </Row>
                    {/* Hidden field for address_2 if needed or add UI for it */}
                    <Form.Item name="address_2" hidden><Input /></Form.Item>

                    {/* Customer field is usually read-only or pre-filled in this context, 
                     but based on the screenshot, it shows a dropdown or input. 
                     Since we are adding to a specific customer, maybe display it as text?
                     For now, I'll omit it or display it if I had the customer name. 
                     The payload uses organization ID which is passed in.
                  */}

                    <Row justify="end" gutter={16}>
                        {isEdit ? (
                            <>
                                <Col>
                                    <Button onClick={onCancel} icon={<BackwardOutlined />}>
                                        Back
                                    </Button>
                                </Col>
                                <Col>
                                    <Button onClick={() => form.resetFields()} icon={<RetweetOutlined />}>
                                        Reset
                                    </Button>
                                </Col>
                                <Col>
                                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                                        Update
                                    </Button>
                                </Col>
                            </>
                        ) : (
                            <>
                                <Col>
                                    <Button onClick={onCancel}>
                                        Cancel
                                    </Button>
                                </Col>
                                <Col>
                                    <Button type="primary" htmlType="submit" loading={loading}>
                                        ADD
                                    </Button>
                                </Col>
                            </>
                        )}
                    </Row>
                </Form>
            </div>
        </Modal>
    );
};

export default AddLocationModal;
 